import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';
import { ConfirmDialog, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../../i18n';
import type { AdminMember } from '../../../hooks/useAdminMembers';
import {
  useConfirmEchelon,
  type AdminPendingEchelon,
  type ConfirmEchelonResponse,
} from '../../../hooks/useAdminRemunerationConfig';

export function PendingEchelonsSection({
  pending,
  maxScore,
  members,
  onError,
  onClear,
  onSuccess,
  onUnchanged,
}: {
  pending: AdminPendingEchelon[];
  maxScore: number | null;
  members: AdminMember[];
  onError: (err: unknown) => void;
  onClear: () => void;
  onSuccess: () => void;
  onUnchanged: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmEchelon();
  const [confirmFor, setConfirmFor] = useState<AdminPendingEchelon | null>(null);

  const nameFor = (email: string): string =>
    members.find((m) => m.email === email)?.fullName?.trim() || email;

  const confirmable = pending.filter((p) => !p.offGrid);
  const skipped = pending.length - confirmable.length;

  function doConfirm() {
    if (!confirmFor) return;
    onClear();
    confirm.mutate(
      { email: confirmFor.email, year: confirmFor.year },
      {
        onSuccess: (res: ConfirmEchelonResponse) => {
          setConfirmFor(null);
          if (res.applied) onSuccess();
          else onUnchanged();
        },
        onError: (err: unknown) => {
          setConfirmFor(null);
          onError(err);
        },
      },
    );
  }

  if (confirmable.length === 0) {
    return (
      <p className="text-meta text-slate-500 dark:text-slate-400">
        {skipped > 0
          ? t('gest:admin.remuneration.config.pending.emptyLineSkipped', { n: skipped })
          : t('gest:admin.remuneration.config.pending.emptyLine')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-body font-semibold text-slate-800 dark:text-slate-100">
        {t('gest:admin.remuneration.config.pending.title')}
      </h3>
      <p className="text-meta text-slate-500 dark:text-slate-400">
        {t('gest:admin.remuneration.config.pending.description')}
      </p>
      <ul className="flex flex-col gap-2">
        {confirmable.map((p) => {
          const profileHref = `/profile/${encodeURIComponent(p.email)}?tab=gestion&from=admin-remuneration`;
          return (
            <li
              key={p.evaluationId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <div className="min-w-0">
                <Link
                  to={profileHref}
                  className="text-field font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  {nameFor(p.email)}
                </Link>
                <p className="text-chip text-slate-500 dark:text-slate-400">
                  {t('gest:admin.remuneration.config.pending.line', {
                    year: p.year,
                    score: maxScore != null ? `${p.score}/${maxScore}` : String(p.score),
                    date: p.submittedAt.slice(0, 10),
                  })}
                </p>
              </div>
              <button
                type="button"
                className={GS_COMPONENTS.btnAction}
                onClick={() => setConfirmFor(p)}
              >
                <BadgeCheck size={16} aria-hidden="true" className="mr-1.5 inline" />
                {t('gest:admin.remuneration.config.pending.confirm')}
              </button>
            </li>
          );
        })}
      </ul>
      {skipped > 0 ? (
        <p className="text-chip text-amber-700 dark:text-amber-300">
          {t('gest:admin.remuneration.config.pending.skippedOffGrid', { n: skipped })}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirmFor != null}
        variant="warning"
        title={t('gest:admin.remuneration.config.pending.dialogTitle')}
        message={t('gest:admin.remuneration.config.pending.dialogMessage', {
          name: confirmFor ? nameFor(confirmFor.email) : '',
          year: confirmFor?.year ?? '',
        })}
        confirmLabel={t('gest:admin.remuneration.config.pending.confirm')}
        cancelLabel={t('gest:management.common.cancel')}
        isLoading={confirm.isPending}
        onConfirm={doConfirm}
        onCancel={() => setConfirmFor(null)}
      />
    </div>
  );
}
